/**
 * События ActionCable Chatwoot → действия над кэшем.
 *
 * Перечень и смысл — по панели Chatwoot: app/javascript/dashboard/helper/actionCable.js,
 * полезная нагрузка — app/listeners/action_cable_listener.rb. Здесь только разбор (без сети и
 * React), поэтому он проверяется тестами.
 */
import {
  normalizeConversation, type CwMessage, type RawConversation,
} from '@/api/inboxTypes';
import type { ConvPatch } from './convList';
import { upsertServerMessage, type MessagesState } from './messageStore';

export type RtAction =
  | { type: 'conv'; patch: ConvPatch }
  | { type: 'message'; convId: number; message: CwMessage }
  | { type: 'unreadCounts' }
  | { type: 'inboxes' };

/** События, несущие полный снимок переписки (Conversations::EventDataPresenter#push_data). */
const SNAPSHOT_EVENTS = new Set([
  'conversation.created',
  'conversation.updated',
  'conversation.status_changed',
  'conversation.read',
  'assignee.changed',
  'team.changed',
  'conversation.contact_changed',
]);

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}

/** Разбор одного события. Чужой кабинет и неизвестные события — пустой список. */
export function decodeEvent(event: string, data: unknown, accountId: number): RtAction[] {
  if (!isObject(data)) return [];
  const evAccount = data.account_id;
  if (typeof evAccount === 'number' && evAccount !== accountId) return [];

  if (event === 'message.created' || event === 'message.updated') {
    const message = data as unknown as CwMessage;
    const convId = message.conversation_id;
    if (typeof convId !== 'number' || typeof message.id !== 'number') return [];
    return [
      {
        type: 'conv',
        patch: event === 'message.created'
          ? { kind: 'message', convId, message }
          : { kind: 'messageUpdated', convId, message },
      },
      { type: 'message', convId, message },
    ];
  }

  if (SNAPSHOT_EVENTS.has(event)) {
    const raw = data as unknown as RawConversation;
    if (typeof raw.id !== 'number' || typeof raw.inbox_id !== 'number') return [];
    return [{ type: 'conv', patch: { kind: 'snapshot', conv: normalizeConversation(raw, accountId) } }];
  }

  if (event === 'conversation.unread_count_changed') return [{ type: 'unreadCounts' }];
  if (event === 'account.cache_invalidated') return [{ type: 'inboxes' }];
  return [];
}

/**
 * Лента открытой переписки по событию. Нет загруженной ленты — ничего не создаём:
 * при открытии она загрузится целиком.
 */
export function applyMessageAction(state: MessagesState | undefined, message: CwMessage): MessagesState | undefined {
  if (!state) return undefined;
  return upsertServerMessage(state, message);
}
